import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Calculator, Flame, Dumbbell, Droplets, Salad, RefreshCw } from "lucide-react";
import { useLocale } from "../../context/LocaleContext";
import { track } from "../../lib/analytics";
import { getGoal } from "../../lib/goal";
import Seo from "../../components/Seo";
import { Breadcrumbs } from "../../components/Breadcrumbs";
import { Mascot } from "../../components/Mascot";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

const ICONS = { bmi: Calculator, calories: Flame, protein: Dumbbell, hydration: Droplets, macros: Salad };
const ORDER = ["bmi", "calories", "protein", "hydration", "macros"];

const META = {
  bmi: {
    title: { en: "BMI calculator", es: "Calculadora de IMC", nl: "BMI-calculator" },
    desc: { en: "Estimate your Body Mass Index and understand what it means.", es: "Estima tu Índice de Masa Corporal y entiende qué significa.", nl: "Schat je Body Mass Index en begrijp wat het betekent." },
    intro: { en: "BMI is a simple screening ratio of weight to height. It doesn't measure body fat, muscle or health directly — athletes and many others fall outside typical ranges. Use it as a rough starting point, not a diagnosis.", es: "El IMC es una relación simple entre peso y altura. No mide grasa, músculo ni salud directamente — atletas y muchas personas quedan fuera de los rangos típicos. Úsalo como punto de partida, no como diagnóstico.", nl: "BMI is een eenvoudige verhouding tussen gewicht en lengte. Het meet geen vet, spieren of gezondheid — sporters en anderen vallen vaak buiten de gebruikelijke ranges. Gebruik het als ruw startpunt, geen diagnose." },
  },
  calories: {
    title: { en: "Daily calorie needs", es: "Calorías diarias", nl: "Dagelijkse calorieën" },
    desc: { en: "Estimate your maintenance calories using the Mifflin-St Jeor equation.", es: "Estima tus calorías de mantenimiento con la ecuación de Mifflin-St Jeor.", nl: "Schat je onderhoudscalorieën met de Mifflin-St Jeor-formule." },
    intro: { en: "This uses the Mifflin-St Jeor equation for BMR and an activity multiplier for TDEE. All values are estimates that vary between individuals. LIVYVIO does not recommend extreme deficits or surpluses.", es: "Usa la ecuación de Mifflin-St Jeor para el TMB y un multiplicador de actividad para el GET. Son estimaciones que varían entre personas. LIVYVIO no recomienda déficits o superávits extremos.", nl: "Gebruikt de Mifflin-St Jeor-formule voor BMR en een activiteitsfactor voor TDEE. Waarden zijn schattingen die per persoon verschillen. LIVYVIO raadt extreme tekorten of overschotten af." },
  },
  protein: {
    title: { en: "Daily protein needs", es: "Proteína diaria", nl: "Dagelijkse eiwitbehoefte" },
    desc: { en: "Get an educational daily protein range based on your weight and activity.", es: "Obtén un rango diario de proteína según tu peso y actividad.", nl: "Krijg een dagelijkse eiwit-range op basis van gewicht en activiteit." },
    intro: { en: "Protein needs are a range, not one exact number. They vary by age, training, diet and individual circumstances. This tool gives a general educational estimate.", es: "Las necesidades de proteína son un rango, no un número exacto. Varían por edad, entrenamiento, dieta y circunstancias individuales. Esta herramienta da una estimación educativa general.", nl: "Eiwitbehoefte is een range, geen exact getal. Het varieert per leeftijd, training, dieet en individuele omstandigheden. Deze tool geeft een algemene, educatieve schatting." },
  },
  hydration: {
    title: { en: "Daily hydration", es: "Hidratación diaria", nl: "Dagelijkse hydratatie" },
    desc: { en: "Estimate a general daily water intake range.", es: "Estima un rango general de ingesta diaria de agua.", nl: "Schat een algemene dagelijkse waterinname-range." },
    intro: { en: "This is general guidance only. Real needs vary considerably with exercise intensity, climate, health and individual factors. Listen to your body and drink to thirst.", es: "Es solo orientación general. Las necesidades reales varían mucho según intensidad del ejercicio, clima, salud y factores individuales. Escucha a tu cuerpo y bebe según la sed.", nl: "Dit is alleen algemene richtlijn. Werkelijke behoefte varieert sterk met intensiteit, klimaat, gezondheid en individuele factoren. Luister naar je lichaam en drink naar dorst." },
  },
  macros: {
    title: { en: "Macronutrients", es: "Macronutrientes", nl: "Macronutriënten" },
    desc: { en: "Turn a calorie target into an illustrative protein / carb / fat split.", es: "Convierte un objetivo de calorías en una distribución ilustrativa de proteína / carbohidratos / grasa.", nl: "Zet een calorie-doel om in een illustratieve eiwit- / koolhydraat- / vetverdeling." },
    intro: { en: "This is an illustrative planning tool, not a medical nutrition prescription. Distributions are common starting points you can adjust to your preferences.", es: "Es una herramienta de planificación ilustrativa, no una prescripción nutricional médica. Las distribuciones son puntos de partida comunes que puedes ajustar.", nl: "Dit is een illustratieve planningstool, geen medisch voedingsvoorschrift. Verdelingen zijn gangbare startpunten die je kunt aanpassen." },
  },
};

const ACTIVITY = { sedentary: 1.2, light: 1.375, moderate: 1.55, active: 1.725, very: 1.9 };
const ACT_LABEL = {
  en: { sedentary: "Sedentary", light: "Light", moderate: "Moderate", active: "Active", very: "Very active" },
  es: { sedentary: "Sedentario", light: "Ligero", moderate: "Moderado", active: "Activo", very: "Muy activo" },
  nl: { sedentary: "Zittend", light: "Licht", moderate: "Matig", active: "Actief", very: "Zeer actief" },
};
const GOAL_OPTS = {
  en: { maintain: "Maintain", lose: "Lose weight", gain: "Gain weight" },
  es: { maintain: "Mantener", lose: "Perder peso", gain: "Ganar peso" },
  nl: { maintain: "Behouden", lose: "Afvallen", gain: "Aankomen" },
};
const SEX = { en: { male: "Male", female: "Female" }, es: { male: "Hombre", female: "Mujer" }, nl: { male: "Man", female: "Vrouw" } };

// Home goal ids -> calculator goal option values.
const GOAL_KEY_MAP = { goalLose: "lose", goalMuscle: "gain", goalPerf: "gain", goalEat: "maintain", goalFit: "maintain", goalSleep: "maintain", goalFeel: "maintain" };
const mappedGoal = () => GOAL_KEY_MAP[getGoal()] || "maintain";

const UNIT_LABEL = {
  height: {
    metric: { en: "Height (cm)", es: "Altura (cm)", nl: "Lengte (cm)" },
    imperial: { en: "Height (in)", es: "Altura (in)", nl: "Lengte (in)" },
  },
  weight: {
    metric: { en: "Weight (kg)", es: "Peso (kg)", nl: "Gewicht (kg)" },
    imperial: { en: "Weight (lb)", es: "Peso (lb)", nl: "Gewicht (lb)" },
  },
};
const RESULT_LABEL = {
  bmr: { en: "BMR", es: "TMB", nl: "BMR" },
  maintenance: { en: "Maintenance", es: "Mantenimiento", nl: "Onderhoud" },
  goalTarget: { en: "Goal target", es: "Objetivo", nl: "Doel" },
  protein: { en: "Protein", es: "Proteína", nl: "Eiwit" },
  carbs: { en: "Carbs", es: "Carbohidratos", nl: "Koolhydraten" },
  fat: { en: "Fat", es: "Grasa", nl: "Vet" },
};

function toMetric(unit, height, weight) {
  const h = parseFloat(height) || 0, w = parseFloat(weight) || 0;
  if (unit === "imperial") return { cm: h * 2.54, kg: w * 0.453592 };
  return { cm: h, kg: w };
}

export function CalculatorsIndex() {
  const { t, lp, sp, locale, sections } = useLocale();
  const paths = useMemo(() => {
    const c = sections.find((s) => s.slug === "calculators");
    return c?.paths || { en: "tools", es: "herramientas", nl: "tools" };
  }, [sections]);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const seo = {
    title: `${t("navTools")} | LIVYVIO`,
    meta_description: t("toolsSub"),
    canonical: `${origin}/${locale}/${paths[locale]}`,
    hreflang: [
      ...Object.entries(paths).map(([l, p]) => ({ locale: l, href: `${origin}/${l}/${p}` })),
      { locale: "x-default", href: `${origin}/en/${paths.en}` },
    ],
    open_graph: { title: t("toolsTitle"), description: t("toolsSub"), type: "website", site_name: "LIVYVIO" },
    jsonld: [{ "@context": "https://schema.org", "@type": "CollectionPage", name: t("toolsTitle"), description: t("toolsSub") }],
  };
  return (
    <>
      <Seo seo={seo} />
      <section className="border-b border-slate-200 bg-slate-50">
        <div className="container-page py-12">
          <Breadcrumbs items={[{ name: "LIVYVIO", to: lp("") }, { name: t("navTools") }]} />
          <h1 className="mt-4 font-heading text-4xl font-black tracking-tight text-slate-900">{t("toolsTitle")}</h1>
          <p className="mt-3 max-w-2xl text-slate-600">{t("toolsSub")}</p>
        </div>
      </section>
      <section className="container-page py-10">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {ORDER.map((key) => {
            const Ico = ICONS[key];
            return (
              <Link key={key} to={lp(`/${sp("calculators")}/${key}`)} data-testid={`calc-card-${key}`}
                className="group flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-6 transition-all hover:-translate-y-1 hover:border-emerald-300 hover:shadow-md">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 group-hover:bg-emerald-600 group-hover:text-white"><Ico className="h-6 w-6" /></span>
                <div>
                  <h3 className="font-heading text-lg font-bold text-slate-900">{META[key].title[locale]}</h3>
                  <p className="mt-1 text-sm text-slate-500">{META[key].desc[locale]}</p>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </>
  );
}

function Field({ label, children }) {
  return <div><Label className="text-sm">{label}</Label><div className="mt-1.5">{children}</div></div>;
}
const selCls = "h-10 w-full rounded-md border border-slate-200 px-3 text-sm";

export function CalculatorPage() {
  const params = useParams();
  const raw = params.calc || params.slug;
  const key = ORDER.includes(raw) ? raw : "bmi";
  const { locale, t, lp, sp, sections, setPageAlternates } = useLocale();
  const [unit, setUnit] = useState("metric");
  const [f, setF] = useState({ sex: "male", age: 30, height: "", weight: "", activity: "moderate", goal: mappedGoal(), calories: "", duration: 30 });
  const [result, setResult] = useState(null);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const canonicalPaths = useMemo(() => {
    const c = sections.find((s) => s.slug === "calculators");
    return c?.paths || { en: "tools", es: "herramientas", nl: "tools" };
  }, [sections]);

  useEffect(() => {
    setPageAlternates(Object.fromEntries(
      Object.entries(canonicalPaths).map(([l, p]) => [l, `/${l}/${p}/${key}`])
    ));
  }, [key, canonicalPaths, setPageAlternates]);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const seo = {
    title: `${META[key].title[locale]} | LIVYVIO`,
    meta_description: META[key].desc[locale],
    canonical: `${origin}/${locale}/${canonicalPaths[locale]}/${key}`,
    hreflang: [
      ...Object.entries(canonicalPaths).map(([l, p]) => ({ locale: l, href: `${origin}/${l}/${p}/${key}` })),
      { locale: "x-default", href: `${origin}/en/${canonicalPaths.en}/${key}` },
    ],
    open_graph: { title: META[key].title[locale], description: META[key].desc[locale], type: "website", site_name: "LIVYVIO" },
    jsonld: [{ "@context": "https://schema.org", "@type": "WebApplication", name: META[key].title[locale], applicationCategory: "HealthApplication", description: META[key].desc[locale] }],
  };

  function compute() {
    track("calculator_started", { locale, props: { calculator: key, goal: getGoal() || null } });
    const { cm, kg } = toMetric(unit, f.height, f.weight);
    let r = null;
    if (key === "bmi") {
      const m = cm / 100;
      if (m > 0 && kg > 0) {
        const bmi = kg / (m * m);
        const cat = bmi < 18.5 ? { en: "Underweight", es: "Bajo peso", nl: "Ondergewicht" }
          : bmi < 25 ? { en: "Healthy range", es: "Rango saludable", nl: "Gezond bereik" }
          : bmi < 30 ? { en: "Overweight", es: "Sobrepeso", nl: "Overgewicht" }
          : { en: "Obesity", es: "Obesidad", nl: "Obesitas" };
        r = { primary: bmi.toFixed(1), label: cat[locale] };
      }
    } else if (key === "calories") {
      const a = parseFloat(f.age) || 0;
      if (cm > 0 && kg > 0 && a > 0) {
        const bmr = 10 * kg + 6.25 * cm - 5 * a + (f.sex === "male" ? 5 : -161);
        const tdee = bmr * ACTIVITY[f.activity];
        const adj = f.goal === "lose" ? tdee - 400 : f.goal === "gain" ? tdee + 300 : tdee;
        r = { primary: Math.round(tdee), unit: "kcal",
              rows: [[RESULT_LABEL.bmr[locale], `${Math.round(bmr)} kcal`], [RESULT_LABEL.maintenance[locale], `${Math.round(tdee)} kcal`], [RESULT_LABEL.goalTarget[locale], `${Math.round(adj - 100)}–${Math.round(adj + 100)} kcal`]] };
      }
    } else if (key === "protein") {
      if (kg > 0) {
        const low = f.activity === "sedentary" ? 0.8 : f.activity === "light" ? 1.0 : 1.4;
        const high = f.goal === "gain" || f.activity === "very" ? 2.2 : 1.8;
        r = { primary: `${Math.round(kg * low)}–${Math.round(kg * high)}`, unit: "g/day" };
      }
    } else if (key === "hydration") {
      if (kg > 0) {
        const dur = parseFloat(f.duration) || 0;
        const base = kg * 30, high = kg * 35 + (dur / 30) * 350;
        r = { primary: `${(base / 1000).toFixed(1)}–${(high / 1000).toFixed(1)}`, unit: "L/day" };
      }
    } else if (key === "macros") {
      const cal = parseFloat(f.calories) || 0;
      if (cal > 0) {
        const split = f.goal === "lose" ? [0.35, 0.35, 0.30] : f.goal === "gain" ? [0.30, 0.45, 0.25] : [0.30, 0.40, 0.30];
        const [p, c, ft] = split;
        r = { primary: `${cal} kcal`,
              rows: [[RESULT_LABEL.protein[locale], `${Math.round(cal * p / 4)} g (${Math.round(p * 100)}%)`], [RESULT_LABEL.carbs[locale], `${Math.round(cal * c / 4)} g (${Math.round(c * 100)}%)`], [RESULT_LABEL.fat[locale], `${Math.round(cal * ft / 9)} g (${Math.round(ft * 100)}%)`]] };
      }
    }
    setResult(r);
    if (r) track("calculator_completed", { locale, props: { calculator: key, goal: getGoal() || null } });
  }

  const showHW = true;
  const Ico = ICONS[key];

  return (
    <>
      <Seo seo={seo} />
      <section className="border-b border-slate-200 bg-slate-50">
        <div className="container-page py-10">
          <Breadcrumbs items={[{ name: "LIVYVIO", to: lp("") }, { name: t("navTools"), to: lp(`/${sp("calculators")}`) }, { name: META[key].title[locale] }]} />
          <div className="mt-4 flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-600 text-white"><Ico className="h-6 w-6" /></span>
            <h1 className="font-heading text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">{META[key].title[locale]}</h1>
          </div>
        </div>
      </section>

      <section className="container-page py-10">
        <div className="grid gap-8 lg:grid-cols-2">
          {/* Inputs */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            {(key === "bmi" || key === "calories" || key === "protein" || key === "hydration") && (
              <div className="mb-5 inline-flex rounded-full border border-slate-200 p-1">
                {["metric", "imperial"].map((u) => (
                  <button key={u} onClick={() => setUnit(u)} data-testid={`unit-${u}`}
                    className={`rounded-full px-4 py-1.5 text-sm font-semibold ${unit === u ? "bg-emerald-600 text-white" : "text-slate-500"}`}>
                    {u === "metric" ? t("unitMetric") : t("unitImperial")}
                  </button>
                ))}
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              {(key === "calories") && (
                <>
                  <Field label={locale === "es" ? "Sexo" : locale === "nl" ? "Geslacht" : "Sex"}>
                    <select className={selCls} value={f.sex} onChange={set("sex")} data-testid="calc-sex">
                      {Object.entries(SEX[locale]).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </Field>
                  <Field label={locale === "es" ? "Edad" : locale === "nl" ? "Leeftijd" : "Age"}>
                    <Input type="number" value={f.age} onChange={set("age")} data-testid="calc-age" />
                  </Field>
                </>
              )}
              {(key === "bmi" || key === "calories") && (
                <Field label={UNIT_LABEL.height[unit][locale]}>
                  <Input type="number" value={f.height} onChange={set("height")} data-testid="calc-height" />
                </Field>
              )}
              {(key === "bmi" || key === "calories" || key === "protein" || key === "hydration") && (
                <Field label={UNIT_LABEL.weight[unit][locale]}>
                  <Input type="number" value={f.weight} onChange={set("weight")} data-testid="calc-weight" />
                </Field>
              )}
              {(key === "calories" || key === "protein") && (
                <Field label={locale === "es" ? "Actividad" : locale === "nl" ? "Activiteit" : "Activity"}>
                  <select className={selCls} value={f.activity} onChange={set("activity")} data-testid="calc-activity">
                    {Object.entries(ACT_LABEL[locale]).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </Field>
              )}
              {(key === "calories" || key === "protein" || key === "macros") && (
                <Field label={locale === "es" ? "Objetivo" : locale === "nl" ? "Doel" : "Goal"}>
                  <select className={selCls} value={f.goal} onChange={set("goal")} data-testid="calc-goal">
                    {Object.entries(GOAL_OPTS[locale]).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </Field>
              )}
              {key === "hydration" && (
                <Field label={locale === "es" ? "Ejercicio (min)" : locale === "nl" ? "Beweging (min)" : "Activity (min)"}>
                  <Input type="number" value={f.duration} onChange={set("duration")} data-testid="calc-duration" />
                </Field>
              )}
              {key === "macros" && (
                <Field label={locale === "es" ? "Calorías diarias" : locale === "nl" ? "Dagelijkse calorieën" : "Daily calories"}>
                  <Input type="number" value={f.calories} onChange={set("calories")} data-testid="calc-calories" />
                </Field>
              )}
            </div>
            <Button onClick={compute} className="mt-6 w-full rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="calc-submit">{t("calculate")}</Button>
          </div>

          {/* Result */}
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
            {result ? (
              <div data-testid="calc-result">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold uppercase tracking-wide text-slate-400">{t("calcResult")}</span>
                  <Mascot className="h-12 w-12" floaty />
                </div>
                <div className="mt-3 font-heading text-5xl font-black text-emerald-600">{result.primary}{result.unit ? <span className="ml-1 text-xl text-slate-400">{result.unit}</span> : null}</div>
                {result.label && <div className="mt-1 text-lg font-semibold text-slate-700">{result.label}</div>}
                {result.rows && (
                  <div className="mt-4 space-y-2">
                    {result.rows.map(([k, v]) => (
                      <div key={k} className="flex justify-between rounded-lg bg-white px-3 py-2 text-sm"><span className="text-slate-500">{k}</span><span className="font-semibold text-slate-800">{v}</span></div>
                    ))}
                  </div>
                )}
                <p className="mt-4 text-xs text-slate-400">{t("calcEstimate")}</p>
                <Button variant="outline" onClick={() => setResult(null)} className="mt-4 rounded-full border-slate-300" data-testid="calc-reset"><RefreshCw className="mr-1.5 h-4 w-4" />{t("calcRecalculate")}</Button>
              </div>
            ) : (
              <div className="flex h-full flex-col items-center justify-center py-10 text-center">
                <Mascot className="h-20 w-20" floaty />
                <p className="mt-4 max-w-xs text-sm text-slate-500">{META[key].desc[locale]}</p>
              </div>
            )}
          </div>
        </div>

        {/* Indexable educational text + disclaimer */}
        <div className="mt-10 max-w-3xl">
          <p className="leading-relaxed text-slate-600">{META[key].intro[locale]}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to={lp(`/${sp("goals")}`)} className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-testid="calc-goals-link">{t("calcGoals")}</Link>
            <Link to={lp(`/${sp("articles")}`)} className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-testid="calc-related-link">{t("calcRelated")}</Link>
          </div>
        </div>
      </section>
    </>
  );
}
